import React, { forwardRef, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';
import { motion } from 'framer-motion';
import 'swiper/css';
import 'swiper/css/effect-coverflow';
import { Autoplay, EffectCoverflow, Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import { AnimatedText } from '@/blocks/animations/animated-text';
import { Container } from '@/blocks/components/Container';
import { ProductSlideCard } from '@/blocks/components/ProductSlider/ProductCard';
import { SlideNext, SlidePrev } from '@/blocks/components/Slider/Arrows';
import '@/blocks/components/Slider/Pagination/swiperPagination.css';
import { CenterHeading, Section, VStack } from '@/blocks/components/layout';
import { useScreenSize } from '@/hooks/useScreenSize';
import typography from '@/styles/typography.module.scss';

import './style.scss';
import styles from './styles.module.scss';
import { ProductSliderProps } from './types';

const MotionSection = motion(Section);

// eslint-disable-next-line react/display-name
export const ProductSliderDesktop = forwardRef<HTMLDivElement, ProductSliderProps>((props, _) => {
  const { title, subtitle, products, controls, sectionClassName } = props;

  const productsRef = useRef<HTMLDivElement>(null);
  const [productScale, setProductScale] = useState(1);

  const { height, width } = useScreenSize();

  useEffect(() => {
    if (width && width > 992) {
      const blockHeight = productsRef?.current?.clientHeight;
      if (!blockHeight || !height) {
        return;
      }
      setProductScale(height < blockHeight ? height / blockHeight : 1);
    } else {
      setProductScale(1);
    }
  }, [height, width]);

  return (
    <MotionSection
      ref={productsRef}
      className={classNames(sectionClassName)}
      initial={{ scale: 1 }}
      animate={{ scale: productScale }}
      transition={{ duration: 0.3 }}
    >
      <VStack>
        <Container>
          <CenterHeading>
            <AnimatedText el={'h2'} className={classNames(typography.h2)}>
              {title}
            </AnimatedText>
            <p className={classNames(typography.subheadingSM, typography.headerSubhead)}>{subtitle}</p>
          </CenterHeading>
        </Container>

        <div className={styles.container}>
          <Swiper
            className="product-slider"
            modules={[EffectCoverflow, Pagination, Navigation, Autoplay]}
            effect={'coverflow'}
            pagination={{ el: `.${styles.pagination}`, clickable: true }}
            navigation={{
              prevEl: `.${styles.prevSlide}`,
              nextEl: `.${styles.nextSlide}`,
            }}
            autoplay={{
              delay: 2500,
              pauseOnMouseEnter: true,
            }}
            loop={true}
            initialSlide={1}
            slidesPerView={1.1}
            centeredSlides={true}
            spaceBetween={8}
            speed={400}
            coverflowEffect={{
              rotate: 0,
              stretch: 0,
              depth: 0,
              modifier: 1,
              slideShadows: false,
            }}
            breakpoints={{
              768: {
                slidesPerView: 2,
                spaceBetween: 30,
                centeredSlides: false,
                coverflowEffect: {
                  rotate: 0,
                  stretch: 0,
                  depth: 162,
                  modifier: 1,
                  slideShadows: false,
                },
              },

              992: {
                slidesPerView: 3,
                spaceBetween: 52,
                centeredSlides: true,
                coverflowEffect: {
                  rotate: 0,
                  stretch: 0,
                  depth: 162,
                  modifier: 1,
                  slideShadows: false,
                },
              },
            }}
          >
            {products.map((product) => {
              return (
                <SwiperSlide key={String(product.title)} className={styles.slide}>
                  {({ isActive, isVisible }) => (
                    <ProductSlideCard
                      {...product}
                      isActive={isActive}
                      isVisible={isVisible}
                      controls={controls}
                    />
                  )}
                </SwiperSlide>
              );
            })}

            <div className={styles.navigation}>
              <SlidePrev variant={'chevron'} className={styles.prevSlide} />
              <SlideNext variant={'chevron'} className={styles.nextSlide} />
            </div>
          </Swiper>

          <div className={styles.pagination}></div>
        </div>
      </VStack>
    </MotionSection>
  );
});
